import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import type { SupplyKey } from '../api/types';
import { affixSummary, rarityLabel, slotLabel } from '../inventory/format';
import { btn, esc, pill, row } from './components';
import { rewardLoadoutPanel } from './loadoutPanel';

const SUPPLIES: Array<{
  key: SupplyKey;
  name: string;
  asset: string;
  effect: (state: AppState) => string;
}> = [
  {
    key: 'oil',
    name: 'Oil Flask',
    asset: '/assets/sprites/items/consumables/supply_oil_flask_01.png',
    effect: (state) => `+${state.merchant?.effects.oilFuel ?? 5} Lantern fuel`,
  },
  {
    key: 'torch',
    name: 'Torch Bundle',
    asset: '/assets/sprites/items/consumables/supply_torch_bundle_01.png',
    effect: (state) => `+${state.merchant?.effects.torchFuel ?? 3} Lantern fuel`,
  },
  {
    key: 'potion',
    name: 'Healing Draught',
    asset: '/assets/sprites/items/consumables/supply_healing_draught_01.png',
    effect: (state) => `+${state.merchant?.effects.potionHeal ?? 4} HP`,
  },
];

function supplyCard(state: AppState, supply: (typeof SUPPLIES)[number]): string {
  const merchant = state.merchant;
  const stock = merchant?.supplies[supply.key] ?? 0;
  const price = merchant?.prices[supply.key] ?? 0;
  return `
    <article class="merchant-supply">
      <img src="${esc(supply.asset)}" alt="" width="64" height="64" loading="lazy" decoding="async">
      <div>
        <h3>${esc(supply.name)}</h3>
        ${row('Owned', `<strong>${esc(stock)}</strong>`)}
        ${row('Price', `${esc(price)} Gold`)}
        ${row('Effect', esc(supply.effect(state)))}
        <div class="merchant-buy">
          <label>Qty <input type="number" min="1" max="99" value="1" data-merchant-qty-for="${esc(supply.key)}"></label>
          ${btn('Buy', 'merchant-buy', { supply: supply.key }, { variant: 'primary', disabled: !merchant })}
        </div>
      </div>
    </article>`;
}

// Merchant "Reward loadout" is informational only: base kit + per-run maximum.
// Paid-extra selection lives on the Dashboard under Descend.
function capsPanel(state: AppState): string {
  return `
    <section class="card merchant-loadout-card">
      ${rewardLoadoutPanel(state, 'Reward loadout', { interactive: false })}
    </section>`;
}

function repairPanel(state: AppState): string {
  const merchant = state.merchant;
  if (!merchant) {
    return `<section class="card merchant-service-card"><h2>Repair / Salvage</h2><p class="muted">Load Merchant status to inspect damaged gear and sellable junk.</p></section>`;
  }
  const items = merchant.repairableItems;
  const hammer = `<img src="${versionedAssetUrl('/assets/sprites/ui/ui_repair_hammer_01.png')}" alt="" width="32" height="32" loading="lazy" decoding="async">`;
  const rows = items.length === 0
    ? '<p class="muted">No damaged owned gear needs repair.</p>'
    : `<div class="repair-list">
        ${items.map((item) => `
          <div class="repair-row">
            <div class="repair-row__icon">${hammer}</div>
            <div>
              <strong>${esc(rarityLabel(item.rarity))} ${esc(slotLabel(item.slotType))}</strong>
              <p class="muted">Durability ${esc(item.durabilityCur)} / ${esc(item.durabilityMax)} · missing ${esc(item.missingDurability)}</p>
            </div>
            <div class="repair-row__cost">${esc(item.repairCost)} Gold</div>
            ${btn('Repair', 'merchant-repair', { item: item.itemInstanceId }, { variant: 'primary', disabled: merchant.goldBalance < item.repairCost })}
          </div>`).join('')}
      </div>`;
  const salvage = salvagePanel(state);
  return `<section class="card merchant-service-card"><h2>Repair</h2>${rows}${salvage}</section>`;
}

function salvagePanel(state: AppState): string {
  const merchant = state.merchant;
  if (!merchant) return '';
  const items = merchant.salvageItems ?? [];
  const ineligible = merchant.salvageIneligibleItems ?? [];
  const rows = items.length === 0
    ? '<p class="muted">No eligible sealed dungeon gear to salvage.</p>'
    : `<div class="repair-list salvage-list">
        ${items.map((item) => `
          <div class="repair-row salvage-row">
            <div>
              <strong>${esc(rarityLabel(item.rarity))} ${esc(slotLabel(item.slotType))}</strong>
              ${item.accountBound ? pill('Account-bound', 'muted') : ''}
              <p class="muted">${esc(affixSummary(item.affixes))} · dura ${esc(item.durabilityCur)} / ${esc(item.durabilityMax)}</p>
            </div>
            <div class="repair-row__cost">${esc(item.salvageGold)} Gold</div>
            ${btn('Salvage', 'merchant-salvage', { item: item.itemInstanceId }, { variant: 'ghost' })}
          </div>`).join('')}
      </div>`;
  const ineligibleRows = ineligible.length === 0
    ? ''
    : `<div class="salvage-ineligible">
        <h3>Not eligible</h3>
        <div class="repair-list">
          ${ineligible.map((item) => `
            <div class="repair-row salvage-ineligible-row">
              <div>
                <strong>${esc(rarityLabel(item.rarity))} ${esc(slotLabel(item.slotType))}</strong>
                <p class="muted"><code>${esc(item.itemInstanceId)}</code></p>
              </div>
              <span class="pill pill--warn" data-status-kind="warn">${esc(item.reasonCode)}</span>
              <p class="muted">${esc(item.reason)}</p>
            </div>`).join('')}
        </div>
      </div>`;
  return `
    <div class="merchant-salvage-block">
      <h2>Sell / Salvage gear</h2>
      <p class="muted">Sell sealed gear to the Merchant for a low fixed Gold return. Affixes do not increase the price. Account-bound gear can be salvaged here, but cannot be listed on the Marketplace.</p>
      ${rows}
      ${ineligibleRows}
    </div>`;
}

export function renderMerchant(state: AppState): string {
  const merchant = state.merchant;
  return `
    <div class="grid merchant-view">
      <section class="card card--wide">
        <div class="section-head">
          <div>
            <h2>Merchant</h2>
            <p class="muted">Supplies are account-bound Gold sinks. Repair restores damaged sealed gear.</p>
          </div>
          <div class="btn-row">${btn('Refresh', 'merchant-refresh', {}, { variant: 'ghost' })}${btn('Back to Waystation', 'goto-waystation', {}, { variant: 'ghost' })}</div>
        </div>
        ${row('Gold balance', merchant ? `<strong>${esc(merchant.goldBalance)}</strong>` : '<span class="muted">not loaded</span>')}
      </section>
      <section class="card card--wide">
        <h2>Supplies</h2>
        <div class="merchant-supplies">
          ${SUPPLIES.map((supply) => supplyCard(state, supply)).join('')}
        </div>
      </section>
      <div class="merchant-lower-grid merchant-lower-grid--services">
        ${capsPanel(state)}
        ${repairPanel(state)}
      </div>
    </div>`;
}
